
import React from 'react';
import { Table } from "@/components/ui/table";
import LeagueHeader from './LeagueHeader';
import LeagueTableBody from './TableBody';
import LeagueTableHeader from './header/LeagueTableHeader';
import LeagueTableControls from './controls/LeagueTableControls';
import LeagueStatsSummary from './stats/LeagueStatsSummary';
import { useLeagueTableStats } from './stats/LeagueTableStats';
import { sortLeagueTableData } from './sorting/LeagueTableSorting';
import { getFilteredMatches } from '@/utils/leagueTableUtils';
import { usePointsDeductions } from '@/hooks/usePointsDeductions';
import { format, isAfter, isBefore, isEqual, parseISO, startOfDay } from 'date-fns';
import { PointsDeductionDialog } from './admin/PointsDeductionDialog';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from '@tanstack/react-query';

interface LeagueTableProps {
  data: any[];
  country: string;
  league: string;
  year: string;
}

export const LeagueTable: React.FC<LeagueTableProps> = ({
  data,
  country,
  league,
  year
}) => {
  const [viewType, setViewType] = React.useState<'all' | 'home' | 'away'>('all');
  const [timeType, setTimeType] = React.useState<'fullTime' | 'halfTime'>('fullTime');
  const [sortColumn, setSortColumn] = React.useState<string>('points');
  const [sortDirection, setSortDirection] = React.useState<'asc' | 'desc'>('desc');
  const [selectedDate, setSelectedDate] = React.useState<Date>();

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  const { data: pointsDeductions } = usePointsDeductions(year);

  const filteredDeductions = React.useMemo(() => {
    console.log('Current league:', league);
    console.log('All deductions:', pointsDeductions);
    
    const filtered = pointsDeductions?.filter(d => {
      console.log('Comparing:', {
        deductionLeague: d.league,
        currentLeague: league,
        deductionSeason: d.season,
        currentSeason: year,
        isMatch: d.season === year && d.league.trim() === league.trim()
      });
      return d.season === year && d.league.trim() === league.trim();
    });

    console.log('Filtered deductions:', filtered);
    return filtered;
  }, [pointsDeductions, year, league]);

  const filteredData = React.useMemo(() => {
    if (!selectedDate) return data;
    
    const selectedDateStart = startOfDay(selectedDate);
    
    return data.filter(match => {
      try {
        const [day, month, year] = match.Date.split('-').map(Number);
        const matchDate = new Date(year, month - 1, day);
        const matchDateStart = startOfDay(matchDate);
        
        return !isAfter(matchDateStart, selectedDateStart);
      } catch (error) {
        console.error('Error parsing date:', match.Date, error);
        return false;
      }
    });
  }, [data, selectedDate]);

  const stats = useLeagueTableStats({ 
    data: filteredData,
    viewType, 
    timeType,
    pointsDeductions: filteredDeductions?.map(d => ({
      team: d.team,
      points: d.points,
      season: d.season
    }))
  });

  const displayData = React.useMemo(() => 
    sortLeagueTableData(stats, { sortColumn, sortDirection }), 
    [stats, sortColumn, sortDirection]
  );

  const { isAdmin } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const handleDeleteDeduction = (deductionId: number) => async () => {
    try {
      const { error } = await supabase
        .from('points_deductions')
        .delete()
        .eq('id', deductionId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Points deduction deleted successfully",
      });

      queryClient.invalidateQueries({ queryKey: ['points-deductions'] });
    } catch (error) {
      console.error('Error deleting points deduction:', error);
      throw error;
    }
  };

  return (
    <div className="space-y-4">
      <LeagueHeader 
        country={country} 
        league={league} 
        year={year}
      />
      
      <LeagueTableControls
        viewType={viewType}
        timeType={timeType}
        onViewTypeChange={(value: 'all' | 'home' | 'away') => setViewType(value)}
        onTimeTypeChange={(value: 'fullTime' | 'halfTime') => setTimeType(value)}
        selectedDate={selectedDate}
        onDateChange={setSelectedDate}
        year={year}
      />

      <div className="w-full overflow-x-auto rounded-md border border-gray-200">
        <div className="min-w-[800px]">
          <Table>
            <LeagueTableHeader 
              onSort={handleSort}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
            />
            <LeagueTableBody 
              sortedData={displayData}
              viewType={viewType}
              getFilteredMatches={getFilteredMatches}
              data={filteredData}
              year={year}
              league={league}
            />
          </Table>
        </div>
      </div>

      {filteredDeductions && filteredDeductions.length > 0 && (
        <div className="mt-4 text-sm text-gray-600">
          <h3 className="font-semibold mb-2 text-center">Points Deductions:</h3>
          {filteredDeductions.map((deduction) => (
            <div key={deduction.id} className="flex items-center justify-center gap-2">
              <p className="text-center">*{deduction.team} - {deduction.points} points: {deduction.reason}</p>
              {isAdmin && (
                <PointsDeductionDialog
                  team={deduction.team}
                  season={year}
                  league={league}
                  deductionId={deduction.id}
                  initialPoints={deduction.points.toString()}
                  initialReason={deduction.reason || ''}
                  isEdit={true}
                  onDelete={handleDeleteDeduction(deduction.id)}
                />
              )}
            </div>
          ))}
          {isAdmin && (
            <div className="flex justify-center mt-2">
              <PointsDeductionDialog
                team=""
                season={year}
                league={league}
              />
            </div>
          )}
        </div>
      )}

      <LeagueStatsSummary data={filteredData} />
    </div>
  );
};

export default LeagueTable;
